import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { AgentProblem, parseAgentProblem } from '../../services/agent-problem';
import { of, Subject, Subscription, timer, takeUntil } from 'rxjs';
import { CacheService } from '../../services/cache.service';
import { catchError, switchMap } from 'rxjs/operators';
import { AgentLoginStart, AgentStatus, ConfigurationService } from '../../configuration/configuration.service';

/**
 * The agent CLI / subscription sign-in: connect the current user's command-line agent
 * (Codex today, Claude soon) by signing in with a ChatGPT account via the
 * device-code flow. This is the user-owned subscription path; the API-key path lives
 * in the agent category's key table (the vault), rendered alongside this card in the
 * AI settings tab. Host availability and connection are shown as state; they never hide
 * the card. Emits {@link connected} once a provider signs in, so a host can continue.
 */
@Component({
  selector: 'app-cli-providers',
  templateUrl: './cli-providers.component.html',
  styleUrls: ['../../configuration/configuration-list.css', './cli-providers.component.css'],
})
export class CliProvidersComponent implements OnInit, OnDestroy {
  /** Fires when a provider becomes signed in (immediate or after device approval). */
  @Output() connected = new EventEmitter<void>();

  agentStatus: AgentStatus | null = null;
  agentLoading = false;
  problem?: AgentProblem;
  connecting = false;
  disconnecting = false;
  device: AgentLoginStart | null = null;   // live device URL + code, while pending
  expired = false;
  connectError: string | null = null;
  copied = false;                          // brief "Copied ✓" feedback on the code
  expanded = false;                        // connected row folds Sign out away; a click on the chevron reveals it
  private readonly destroyed$ = new Subject<void>();
  private statusSub?: Subscription;
  private pollSub?: Subscription;
  private pollDeadlineMs = 0;

  constructor(private configuration: ConfigurationService, cache: CacheService) {
    cache.sessionChanged$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.stopPolling();
      this.agentStatus = null;
      this.device = null;
      this.problem = undefined;
      this.connectError = null;
      this.agentLoading = this.connecting = this.disconnecting = false;
    });
  }

  toggleExpanded(): void { this.expanded = !this.expanded; }

  /** The device code split into per-character cells (OpenAI-style boxes). */
  get codeChars(): string[] { return (this.device?.code || '').split(''); }

  ngOnInit(): void { this.loadAgentStatus(); }
  ngOnDestroy(): void { this.stopPolling(); this.destroyed$.next(); this.destroyed$.complete(); }

  loadAgentStatus(): void {
    this.statusSub?.unsubscribe();
    this.agentStatus = this.configuration.cachedAgentStatus ?? null;
    this.agentLoading = true;
    this.problem = parseAgentProblem(this.agentStatus?.problem);
    this.connectError = null;
    this.statusSub = this.configuration.agentLoginStatus().pipe(takeUntil(this.destroyed$)).subscribe({
      next: s => { this.agentStatus = s; this.problem = parseAgentProblem(s.problem); this.agentLoading = false; },
      error: err => {
        this.agentLoading = false;
        this.agentStatus = null;
        this.problem = parseAgentProblem(err?.error?.problem);
        this.connectError = this.problem?.message ?? 'Could not check the agent connection. Try again.';
      }
    });
  }

  connectAgent(): void {
    this.statusSub?.unsubscribe();
    this.agentLoading = false;
    this.connecting = true;
    this.connectError = null;
    this.problem = undefined;
    this.expired = false;
    this.copied = false;
    this.configuration.agentLoginStart().pipe(takeUntil(this.destroyed$)).subscribe({
      next: r => {
        this.connecting = false;
        this.agentStatus = this.configuration.cachedAgentStatus ?? null;
        this.agentLoading = false;
        if (r.authenticated) { this.device = null; this.loadAgentStatus(); this.connected.emit(); return; }
        this.device = r;
        this.pollDeadlineMs = Date.now() + (r.expiresInSec || 900) * 1000;
        this.startPolling();
      },
      error: (err) => {
        this.connecting = false;
        this.problem = parseAgentProblem(err?.error?.problem);
        this.connectError = this.problem?.message ?? err?.error?.error ?? 'Could not reach the agent.';
      }
    });
  }

  cancelConnect(): void { this.device = null; this.stopPolling(); }

  disconnectAgent(): void {
    this.statusSub?.unsubscribe();
    this.agentLoading = false;
    this.stopPolling();
    this.disconnecting = true;
    this.connectError = null;
    this.configuration.agentLogout().pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => { this.disconnecting = false; this.device = null; this.stopPolling(); this.loadAgentStatus(); },
      error: (err) => { this.disconnecting = false; this.problem = parseAgentProblem(err?.error?.problem); this.connectError = this.problem?.message ?? 'Could not reach the agent.'; }
    });
  }

  copyCode(): void {
    const code = this.device?.code;
    if (!code) return;
    navigator.clipboard?.writeText(code).then(() => {
      this.copied = true;
      setTimeout(() => (this.copied = false), 2000);
    }).catch(() => { /* clipboard blocked — the code is still selectable */ });
  }

  // Poll status until the user approves the device code, or it expires.
  private startPolling(): void {
    this.stopPolling();
    // catchError INSIDE switchMap: a transient status error yields null and the
    // timer keeps firing. Reaching the outer stream would kill the poll for good,
    // so an approved device code would never be noticed.
    this.pollSub = timer(3000, 3000).pipe(
      switchMap(() => this.configuration.agentLoginStatus().pipe(catchError(() => of(null)))),
      takeUntil(this.destroyed$)
    ).subscribe(s => {
      if (s?.authenticated) { this.agentStatus = s; this.device = null; this.stopPolling(); this.connected.emit(); }
      else if (Date.now() > this.pollDeadlineMs) { this.device = null; this.expired = true; this.stopPolling(); }
    });
  }

  private stopPolling(): void { this.pollSub?.unsubscribe(); this.pollSub = undefined; }
}
